export const ORDER_WEIGHT_OPTIONS = [
  "0-1 kg",
  "1-5 kg",
  "5-10 kg",
  "10-15 kg",
  "15-20 kg",
];

const ARRIVED_AT_PICKUP_MESSAGE = "arrived at pickup";
const PICKUP_CLOSED_MESSAGE = "pickup closed";
const CANCELLATION_WINDOW_MS = 60 * 1000;

const parseTimeValue = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return null;

  const twelveHourMatch = normalized.match(
    /^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/
  );
  if (twelveHourMatch) {
    let hours = Number(twelveHourMatch[1]);
    const minutes = Number(twelveHourMatch[2]);
    const meridiem = twelveHourMatch[3].toUpperCase();

    if (meridiem === "PM" && hours < 12) {
      hours += 12;
    }
    if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  }

  const twentyFourHourMatch = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHourMatch) {
    return {
      hours: Number(twentyFourHourMatch[1]),
      minutes: Number(twentyFourHourMatch[2]),
    };
  }

  return null;
};

const formatTwoDigits = (value) => String(value).padStart(2, "0");
export const END_OF_DAY_MINUTES = 23 * 60 + 59;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeDistancePricingItem = (item = {}) => ({
  ...item,
  startKm: toNumber(item.startKm ?? item.start_km),
  endKm: toNumber(item.endKm ?? item.end_km),
  currentPricePerKm: toNumber(
    item.currentPricePerKm ?? item.current_price_per_km
  ),
  start_km: toNumber(item.start_km ?? item.startKm),
  end_km: toNumber(item.end_km ?? item.endKm),
  current_price_per_km: toNumber(
    item.current_price_per_km ?? item.currentPricePerKm
  ),
});

export const normalizePriceManipulation = (pricingInput = {}) => {
  const distancePricingSource = Array.isArray(pricingInput.distancePricing)
    ? pricingInput.distancePricing
    : Array.isArray(pricingInput.distance_pricing)
    ? pricingInput.distance_pricing
    : [];

  const distancePricing = distancePricingSource.map(normalizeDistancePricingItem);
  const perKilometerCharge = toNumber(
    pricingInput.perKilometerCharge ?? pricingInput.per_kilometer_charge
  );
  const additionalPerKilometerCharge = toNumber(
    pricingInput.additionalPerKilometerCharge ??
      pricingInput.additional_per_kilometer_charge
  );
  const additionalPickupCharge = toNumber(
    pricingInput.additionalPickupCharge ?? pricingInput.additional_pickup_charge
  );
  const securityFeesCharges = toNumber(
    pricingInput.securityFeesCharges ?? pricingInput.security_fees_charges
  );
  const baseOrderCharges = toNumber(
    pricingInput.baseOrderCharges ?? pricingInput.base_order_charges
  );
  const instaportCommission = toNumber(
    pricingInput.instaportCommission ?? pricingInput.instaport_commission
  );
  const additionalDropCharge = toNumber(
    pricingInput.additionalDropCharge ?? pricingInput.additional_drop_charge
  );
  const flexibleDeliveryDiscountPercent = toNumber(
    pricingInput.flexibleDeliveryDiscountPercent ??
      pricingInput.flexible_delivery_discount_percent
  );

  return {
    ...pricingInput,
    perKilometerCharge,
    additionalPerKilometerCharge,
    additionalPickupCharge,
    securityFeesCharges,
    baseOrderCharges,
    instaportCommission,
    additionalDropCharge,
    flexibleDeliveryDiscountPercent,
    per_kilometer_charge: perKilometerCharge,
    additional_per_kilometer_charge: additionalPerKilometerCharge,
    additional_pickup_charge: additionalPickupCharge,
    security_fees_charges: securityFeesCharges,
    base_order_charges: baseOrderCharges,
    instaport_commission: instaportCommission,
    additional_drop_charge: additionalDropCharge,
    flexible_delivery_discount_percent: flexibleDeliveryDiscountPercent,
    distancePricing,
    distance_pricing: distancePricing,
  };
};

export const toTimeInputValue = (value) => {
  const parsed = parseTimeValue(value);
  if (!parsed) return "";
  return `${formatTwoDigits(parsed.hours)}:${formatTwoDigits(parsed.minutes)}`;
};

export const timeValueToMinutes = (value) => {
  const parsed = parseTimeValue(value);
  if (!parsed) return null;
  return parsed.hours * 60 + parsed.minutes;
};

export const minutesToTimeValue = (value) => {
  const totalMinutes = Number(value);
  if (!Number.isFinite(totalMinutes)) return "";
  const safeMinutes = Math.min(Math.max(Math.round(totalMinutes), 0), END_OF_DAY_MINUTES);
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${formatTwoDigits(hours)}:${formatTwoDigits(minutes)}`;
};

export const toAmPmTime = (value) => {
  const parsed = parseTimeValue(value);
  if (!parsed) return "";

  let hours = parsed.hours;
  const minutes = parsed.minutes;
  const meridiem = hours >= 12 ? "PM" : "AM";

  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours -= 12;
  }

  return `${hours}:${formatTwoDigits(minutes)} ${meridiem}`;
};

export const addMinutesToTime = (value, minutesToAdd = 45) => {
  const parsed = parseTimeValue(value);
  if (!parsed) return "";

  const totalMinutes = parsed.hours * 60 + parsed.minutes + minutesToAdd;
  const normalizedHours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const normalizedMinutes = totalMinutes % 60;

  return `${formatTwoDigits(normalizedHours)}:${formatTwoDigits(
    normalizedMinutes
  )}`;
};

export const addMinutesToTimeWithinDay = (value, minutesToAdd = 0) => {
  const currentMinutes = timeValueToMinutes(value);
  if (currentMinutes === null) return "";
  const nextMinutes = currentMinutes + Number(minutesToAdd || 0);
  if (nextMinutes > END_OF_DAY_MINUTES) {
    return "";
  }
  return minutesToTimeValue(nextMinutes);
};

export const getLatestStartTimeForDuration = (minutesToAdd = 0) => {
  const duration = Number(minutesToAdd || 0);
  if (!Number.isFinite(duration) || duration < 0 || duration > END_OF_DAY_MINUTES) {
    return "";
  }
  return minutesToTimeValue(END_OF_DAY_MINUTES - duration);
};

export const compareTimeValues = (firstValue, secondValue) => {
  const firstMinutes = timeValueToMinutes(firstValue);
  const secondMinutes = timeValueToMinutes(secondValue);
  if (firstMinutes === null || secondMinutes === null) return null;
  if (firstMinutes === secondMinutes) return 0;
  return firstMinutes > secondMinutes ? 1 : -1;
};

export const getWeightCharge = (parcelWeight) => {
  if (
    parcelWeight === ORDER_WEIGHT_OPTIONS[0] ||
    parcelWeight === ORDER_WEIGHT_OPTIONS[1]
  ) {
    return 0;
  }
  if (parcelWeight === ORDER_WEIGHT_OPTIONS[2]) return 50;
  if (parcelWeight === ORDER_WEIGHT_OPTIONS[3]) return 100;
  return 150;
};

export const getDistancePrice = (distance, pricingInput) => {
  const pricing = normalizePriceManipulation(pricingInput);
  const distancePricing = Array.isArray(pricing.distancePricing)
    ? pricing.distancePricing
    : [];

  if (
    !pricingInput ||
    distancePricing.length === 0
  ) {
    return toNumber(pricing.perKilometerCharge);
  }

  for (const slab of distancePricing) {
    if (distance >= slab.startKm && distance <= slab.endKm) {
      return toNumber(slab.currentPricePerKm);
    }
  }

  return (
    toNumber(distancePricing[distancePricing.length - 1]?.currentPricePerKm)
  );
};

export const calculateSecurityFeeAmount = (parcelValue, securityPercent) => {
  const value = Number(parcelValue) || 0;
  if (value < 1) return 0;
  if (value < 100) return 1;

  const rawFee = (value * (Number(securityPercent) || 0)) / 100;
  if (rawFee < 1) return 0;

  return Math.round(rawFee);
};

export const getWalletDueComponent = (order) => {
  const walletDue = Math.round(
    Number(order?.wallet_due ?? order?.walletDue ?? 0) || 0
  );
  const includedFlag =
    order?.wallet_due_included_in_amount ?? order?.walletDueIncludedInAmount;
  const walletDueIncluded =
    typeof includedFlag === "boolean" ? includedFlag : walletDue > 0;

  return walletDueIncluded && walletDue > 0 ? walletDue : 0;
};

export const normalizeLocationForPayload = (location, deliveryType) => {
  const normalizedTime = toTimeInputValue(
    location?.time || location?.fromtime || ""
  );

  if (deliveryType !== "scheduled") {
    return {
      ...location,
      date: "",
      time: "",
      fromtime: "",
      totime: "",
    };
  }

  const fromtime = toAmPmTime(normalizedTime);
  const totime = toAmPmTime(location?.totime || addMinutesToTime(normalizedTime));

  return {
    ...location,
    time: normalizedTime,
    fromtime,
    totime,
  };
};

export const normalizeLocationsForPayload = (locations, deliveryType) =>
  (locations || []).map((location) =>
    normalizeLocationForPayload(location, deliveryType)
  );

const getStatusMessage = (status) =>
  String(status?.message || "").trim().toLowerCase();

export const getArrivedAtPickupStatus = (order) =>
  (order?.orderStatus || []).find(
    (status) => getStatusMessage(status) === ARRIVED_AT_PICKUP_MESSAGE
  ) || null;

export const canCancelOrderNow = (order, now = Date.now()) => {
  const normalizedStatus = String(order?.status || "").trim().toLowerCase();
  if (normalizedStatus === "cancelled" || normalizedStatus === "delivered") {
    return false;
  }

  const pickupClosed = (order?.orderStatus || []).some(
    (status) => getStatusMessage(status) === PICKUP_CLOSED_MESSAGE
  );
  if (pickupClosed) return false;

  const isAssigned =
    Boolean(order?.rider) || normalizedStatus === "processing";
  if (!isAssigned) return true;

  const arrivedAtPickup = getArrivedAtPickupStatus(order);
  if (!arrivedAtPickup?.timestamp) return true;

  return now - Number(arrivedAtPickup.timestamp) <= CANCELLATION_WINDOW_MS;
};

export const getCancellationWindowRemainingMs = (order, now = Date.now()) => {
  const arrivedAtPickup = getArrivedAtPickupStatus(order);
  if (!arrivedAtPickup?.timestamp) return 0;

  const elapsed = now - Number(arrivedAtPickup.timestamp);
  return Math.max(CANCELLATION_WINDOW_MS - elapsed, 0);
};
